import * as React from 'react';
import { KeyboardEvent } from 'react';
import classNames from 'classnames';
import { SliderGallerySkinProps } from '../SliderGallery.types';
import SliderGalleryItem from '../../SliderGalleryItem/viewer/SliderGalleryItem';
import {
  translations,
  TestIds,
  navButtonsDescriptionElementId,
} from '../constants';
import { useGesture } from '../../../../providers/useGesture/useGesture';
import { keyCodes } from '../../../../core/commons/a11y';
import { generateItemIndexString } from '../../commons/itemUtils';
import { getDataAttributes } from '../../../../core/commons/utils';

const emptyFn = () => {};

const SliderGallery: React.FC<SliderGallerySkinProps> = props => {
  const {
    id,
    className,
    skinsStyle,
    skinsItemStyle,
    items,
    imageOnClickAction,
    openImageZoom = () => {},
    translate,
    aspectRatio,
    onItemClicked,
    keepImageAspectRatio,
    SkinNavButton,
    play = emptyFn,
    pause = emptyFn,
    onMouseEnter,
    onMouseLeave,
    playOnHover,
    playInLoop,
  } = props;

  const [isPressed, setIsPressed] = React.useState<
    Record<'left' | 'right', boolean>
  >({
    right: false,
    left: false,
  });

  const handleKeyDown = (direction: 'left' | 'right', event: KeyboardEvent) => {
    if (event.keyCode === keyCodes.enter && !isPressed[direction]) {
      setIsPressed(currentIsPressed => ({
        ...currentIsPressed,
        [direction]: true,
      }));
      callPlay(direction);
    }
  };

  const handleKeyUp = (direction: 'left' | 'right') => {
    if (isPressed[direction]) {
      setIsPressed(currentIsPressed => ({
        ...currentIsPressed,
        [direction]: false,
      }));
      pause(id);
    }
  };

  const galleryRef = React.useRef<HTMLDivElement>(null);
  const visibleItemsContainerRef = React.useRef<HTMLDivElement>(null);
  const allItemsContainerRef = React.useRef<HTMLDivElement>(null);

  const callPlay = (direction: 'left' | 'right') =>
    play(
      id,
      direction,
      visibleItemsContainerRef.current,
      allItemsContainerRef.current,
    );

  useGesture('onSwipeLeft', () => callPlay('right'), galleryRef);
  useGesture('onSwipeRight', () => callPlay('left'), galleryRef);

  const itemsWithOriginalIndex = items.map((item, index) => ({
    item,
    originalIndex: index,
  }));

  const totalItems = playInLoop
    ? [...itemsWithOriginalIndex, ...itemsWithOriginalIndex]
    : [...itemsWithOriginalIndex];

  const itemsToShow = totalItems.map(({ item, originalIndex }, index) => {
    const itemClickHandler = () => {
      onItemClicked?.({
        itemIndex: originalIndex,
        type: 'itemClicked',
      });
    };

    return (
      <SliderGalleryItem
        key={`item-${index}`}
        extraClassNames={{ root: skinsStyle.item }}
        skinsStyle={skinsItemStyle}
        imageOnClickAction={imageOnClickAction}
        itemId={generateItemIndexString(originalIndex, id)}
        openImageZoom={(dataId: string) => openImageZoom(dataId, id)}
        itemClickHandler={itemClickHandler}
        translate={translate}
        aspectRatio={aspectRatio}
        keepImageAspectRatio={keepImageAspectRatio}
        {...item}
      />
    );
  });

  const navButtonsDescribedById = `${id}-${navButtonsDescriptionElementId}`;

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(className, skinsStyle.root)}
      role="region"
      aria-label={translate!(
        translations.ARIA_LABEL_NAMESPACE,
        translations.SLIDER_GALLERY_ARIA_LABEL_KEY,
        translations.SLIDER_GALLERY_ARIA_LABEL_DEFAULT,
      )}
      ref={galleryRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <p
        id={navButtonsDescribedById}
        className={skinsStyle.playButtonsDescription}
      >
        {translate!(
          translations.ARIA_LABEL_NAMESPACE,
          translations.SLIDER_PLAY_BUTTON_DESCRIPTION_KEY,
          translations.SLIDER_PLAY_BUTTON_DESCRIPTION_DEFAULT,
        )}
      </p>
      <SkinNavButton
        ariaLabel={translate!(
          translations.ARIA_LABEL_NAMESPACE,
          translations.SLIDER_PLAY_BACKWARD_ARIA_LABEL_KEY,
          translations.SLIDER_PLAY_BACKWARD_ARIA_LABEL_DEFAULT,
        )}
        extraClassName={skinsStyle.swipeLeftHitArea}
        direction="left"
        ariaPressed={isPressed.left ? 'true' : 'false'}
        describedById={navButtonsDescribedById}
        {...(playOnHover
          ? {
              mouseEnter: () => callPlay('left'),
              mouseLeave: () => pause(id),
              keyDown: event => handleKeyDown('left', event),
              keyUp: () => handleKeyUp('left'),
            }
          : { touchStart: () => callPlay('left') })}
      />
      <SkinNavButton
        ariaLabel={translate!(
          translations.ARIA_LABEL_NAMESPACE,
          translations.SLIDER_PLAY_FORWARD_ARIA_LABEL_KEY,
          translations.SLIDER_PLAY_FORWARD_ARIA_LABEL_DEFAULT,
        )}
        extraClassName={skinsStyle.swipeRightHitArea}
        direction="right"
        ariaPressed={isPressed.right ? 'true' : 'false'}
        describedById={navButtonsDescribedById}
        {...(playOnHover
          ? {
              mouseEnter: () => callPlay('right'),
              mouseLeave: () => pause(id),
              keyDown: event => handleKeyDown('right', event),
              keyUp: () => handleKeyUp('right'),
            }
          : { touchStart: () => callPlay('right') })}
      />
      <div
        data-testid={TestIds.visibleItemsContainer}
        className={skinsStyle.visibleItemsContainer}
        ref={visibleItemsContainerRef}
        aria-live="polite"
      >
        <div
          id={`items_${id}`}
          data-testid={TestIds.allItemsContainer}
          className={skinsStyle.allItemsContainer}
          ref={allItemsContainerRef}
        >
          {itemsToShow}
        </div>
      </div>
      <div className={skinsStyle.counter} />
      <div className={skinsStyle.autoplay} />
    </div>
  );
};

export default SliderGallery;
